import { VLOGE_TEMELJNIC, type IzidRazvrstitve, type PregledNastavitev, type StanjeRazvrscanja, type VlogaTemeljnice } from '@koda/shared';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api';
import { znesekZValuto } from '../lib/format';
import { TabelaPostavk, TabelaSkupin } from '../sestavni/TabelaNastavitev';
import { UskladitevOtvoritve } from '../sestavni/UskladitevOtvoritve';

/**
 * Nastavitve razvrščanja za eno podjetje.
 *
 * Tabele so enake preglednicam, iz katerih se razvrščanje ureja: vrstica je
 * skupina ali postavka, konti pa našteti v enem polju. Standardna shema velja
 * vsem strankam; kar se tu spremeni, velja samo pri tem podjetju
 * (docs/ARHITEKTURA.md, 4.4).
 *
 * Sprememba poročil ne spremeni sama po sebi — knjižbe je treba ponovno
 * razvrstiti. To je zaveden korak s svojim gumbom.
 */
/** Nazivi vlog vrst temeljnic (5.9 in 15); sifra ostane v vrednosti. */
const NAZIV_VLOGE: Record<VlogaTemeljnice, string> = {
  navadna: 'navadna',
  otvoritev: 'otvoritev',
  zapiranje: 'zapiranje leta (izkaz)',
  zapiranje_bilance: 'zapiranje bilance',
  amortizacija: 'amortizacija (Projekti)',
  popravek_amortizacije: 'popravek amortizacije (Projekti)',
};

/** Kratka pripomba ob gumbu; polno besedilo je v namigu (title). */
const OPIS_RAZVRSCANJA =
  'Upošteva vse nastavitve na tej strani: skupine in postavke izkaza, postavke denarnega toka, vloge vrst temeljnic. ' +
  'Pri več sto tisoč knjižbah traja minuto ali dve.';
const NAMIG_RAZVRSCANJA =
  'Vsako knjižbo podjetja (vsa leta) na novo uvrsti po vseh nastavitvah na tej strani. ' +
  'Razvrščanje teče na strežniku v ozadju: če stran osvežiš ali zapustiš, se vseeno konča, ' +
  'po vrnitvi stran pokaže, da še teče. Dokler teče, ga ni mogoče zagnati znova.';

const knjizb = (n: number): string =>
  `${n.toLocaleString('sl-SI', { useGrouping: 'always' })} ${n === 1 ? 'knjižba' : 'knjižb'}`;

/** Izid ponovne razvrstitve knjižb. */
function IzidRazvrscanja({ izid }: { izid: IzidRazvrstitve }): JSX.Element {
  // Ukrep potrebuje konto, ki ima v katerem obdobju saldo; skupni saldo nic
  // tega ne pove (+1.000 januarja in -1.000 februarja manjka v obeh mesecih).
  const zUkrepom = izid.kontiZUcinkom;
  const sSaldom = (k: string): string => `${k} (${znesekZValuto(izid.saldiBrezSkupine[k] ?? '0.00')})`;
  return (
    <p className={zUkrepom.length === 0 ? 'uspeh-besedilo' : 'opozorilo'}>
      Razvrščenih knjižb: {izid.knjizb.toLocaleString('sl-SI')}, od tega prepisanih{' '}
      {izid.prepisanih.toLocaleString('sl-SI')}
      {izid.prepisanih === 0
        ? ' (razvrstitev se ni spremenila). '
        : izid.praznoMb !== null && izid.praznoMb >= 20
          ? ` (v tabeli je ostalo približno ${izid.praznoMb.toLocaleString('sl-SI')} MB praznega prostora; ponovno se uporabi, disku ga vrne strnitev – Stanje baze). `
          : '. '}
      {izid.kontiBrezSkupine.length === 0
        ? 'Vsak konto pade v neko skupino izkaza.'
        : `Konti brez skupine izkaza: ${izid.kontiBrezSkupine.map(sSaldom).join(', ')}. ` +
          (zUkrepom.length === 0
            ? 'Vsi imajo v vsakem obdobju (otvoritev in vsak mesec vsakega leta) saldo 0, zato v poročilih ne manjkajo in ukrep ni potreben.'
            : `Ukrep je potreben pri ${zUkrepom.length === 1 ? 'kontu' : 'kontih'} ${zUkrepom.join(', ')}: v vsaj enem obdobju imajo saldo, ki v poročilih tega obdobja manjka, tudi če je skupni saldo 0. Dodeli skupino v tabeli zgoraj.`)}
    </p>
  );
}

export function Nastavitve(): JSX.Element {
  const { id = '' } = useParams();
  const [nazivPodjetja, setNazivPodjetja] = useState('');
  const [pregled, setPregled] = useState<PregledNastavitev | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [razvrscam, setRazvrscam] = useState(false);
  const [izid, setIzid] = useState<IzidRazvrstitve | null>(null);
  // Konto za prenos izida se ureja v polju; ob osvezitvi se prepise z veljavnim.
  const [kontoIzida, setKontoIzida] = useState('');

  const osvezi = (): void => {
    api
      .nastavitve(id)
      .then((p) => {
        setPregled(p);
        setKontoIzida(p.prenosIzida.konto ?? '');
        setNapaka(null);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Nastavitev ni bilo mogoče prebrati.');
      });
  };

  useEffect(osvezi, [id]);

  useEffect(() => {
    api
      .podjetja()
      .then((vsa) => {
        setNazivPodjetja(vsa.find((p) => p.id === id)?.naziv ?? '');
      })
      .catch(() => {
        /* naziv ni nujen za delovanje strani */
      });
  }, [id]);

  /** Vsaka sprememba osveži sliko; napake pokažemo takoj, ne ob razvrstitvi. */
  const izvedi = (delo: Promise<unknown>): void => {
    setNapaka(null);
    setIzid(null);
    delo.then(osvezi).catch((e: unknown) => {
      setNapaka(e instanceof Error ? e.message : 'Spremembe ni bilo mogoče shraniti.');
    });
  };

  // Zadnja razlicica osvezi za spremljaj (ki je useCallback in je ne sme imeti v odvisnostih).
  const osveziRef = useRef(osvezi);
  osveziRef.current = osvezi;

  /**
   * Razvrscanje tece na strezniku v ozadju; stran stanje poizveduje, dokler
   * je v teku. Ob odprtju strani se preveri, ali ze tece (npr. po osvezitvi).
   */
  const spremljaj = useCallback(
    (stanje: StanjeRazvrscanja): void => {
      if (stanje.vTeku) {
        setRazvrscam(true);
        window.setTimeout(() => {
          api.stanjeRazvrscanja(id).then(spremljaj).catch((e: unknown) => {
            setRazvrscam(false);
            setNapaka(e instanceof Error ? e.message : 'Stanja razvrščanja ni bilo mogoče prebrati.');
          });
        }, 2000);
        return;
      }
      setRazvrscam(false);
      if (stanje.zadnje?.napaka != null) setNapaka(stanje.zadnje.napaka);
      if (stanje.zadnje?.izid) setIzid(stanje.zadnje.izid);
      // Konec razvrscanja: nastavitve se preberejo znova, da opozorila o
      // zastareli razvrstitvi in neuveljavljeni vlogi ne ostanejo vidna.
      osveziRef.current();
    },
    [id],
  );

  useEffect(() => {
    api
      .stanjeRazvrscanja(id)
      .then((s) => {
        if (s.vTeku) spremljaj(s);
      })
      .catch(() => {
        /* stanje ni nujno za delovanje strani */
      });
  }, [id, spremljaj]);

  const prerazvrsti = (): void => {
    setRazvrscam(true);
    setNapaka(null);
    setIzid(null);
    api
      .prerazvrsti(id)
      .then((zacetno) => {
        // Prvo poizvedovanje hitreje; konec (tudi osvezitev nastavitev) obravnava spremljaj.
        if (zacetno.vTeku) {
          window.setTimeout(() => {
            api.stanjeRazvrscanja(id).then(spremljaj).catch(() => {
              setRazvrscam(false);
            });
          }, 1000);
        } else spremljaj(zacetno);
      })
      .catch((e: unknown) => {
        setRazvrscam(false);
        setNapaka(e instanceof Error ? e.message : 'Knjižb ni bilo mogoče razvrstiti.');
      });
  };

  return (
    <main className="siroko">
      <div className="vsebina">
        <nav className="drobtine">
          <Link to="/">Podjetja</Link>
          <Link to={`/podjetje/${id}`}>{nazivPodjetja || 'Podjetje'}</Link>
        </nav>

        <div className="glava-nastavitev">
          <div>
            <h1>Nastavitve razvrščanja</h1>
            <p className="podnaslov">
              Velja standardna shema; kar spremeniš tukaj, velja samo pri tem podjetju. Spremembe
              pridejo v poročila, ko knjižbe ponovno razvrstiš – gumb je pod vrstami temeljnic.
            </p>
          </div>
        </div>

        {napaka !== null && <p className="napaka">{napaka}</p>}

        {pregled !== null && pregled.razvrstitevZastarela && (
          <p className="opozorilo">
            Pravila so novejša od razvrstitve knjižb. Poročila kažejo stare številke, dokler
            knjižb ponovno ne razvrstiš.
          </p>
        )}


        {pregled === null && napaka === null && <p>Berem nastavitve …</p>}

        {pregled !== null && (
          <>
            <section className="tabela-odsek">
              <h2>Skupine izkaza</h2>
              <p className="namig">
                Razpon kontov, ki pripada skupini. Razponi se ne smejo prekrivati — konto mora
                pripadati natanko eni skupini.
              </p>
              <div className="tabela-ovoj">
                <TabelaSkupin
                  skupine={pregled.skupine}
                  nadskupine={pregled.nadrejene.skupine}
                  naShrani={(skupina, razpon) => {
                    izvedi(api.shraniSkupino(id, skupina, razpon));
                  }}
                  naPonastavi={(skupina) => {
                    izvedi(api.ponastaviSkupino(id, skupina));
                  }}
                  naDodaj={(skupina) => {
                    izvedi(api.dodajSkupino(id, skupina));
                  }}
                />
              </div>
            </section>

            <section className="tabela-odsek">
              <h2>Postavke izkaza</h2>
              <p className="namig">
                Vrstice poenostavljenega izkaza poslovnega izida v Hitrem pregledu. Trimestne
                šifre veljajo pri predponah{' '}
                <strong>{pregled.trimestni.postavke.join(', ') || '(nobeni)'}</strong>, drugod so
                šifre dvomestne.
              </p>
              <div className="tabela-ovoj">
                <TabelaPostavk
                  postavke={pregled.postavke}
                  nadrejene={pregled.nadrejene.postavke}
                  trimestni={pregled.trimestni.postavke}
                  nazivNadrejene="skupina"
                  naShrani={(postavka, konti) => {
                    izvedi(api.shraniPostavko(id, postavka, konti));
                  }}
                  naPonastavi={(postavka) => {
                    izvedi(api.ponastaviPostavko(id, postavka));
                  }}
                  naDodaj={(postavka) => {
                    izvedi(api.dodajPostavko(id, postavka));
                  }}
                />
              </div>
            </section>

            <section className="tabela-odsek">
              <h2>Postavke denarnega toka</h2>
              <p className="namig">
                Trimestne šifre veljajo pri predponah{' '}
                <strong>{pregled.trimestni.cf.join(', ') || '(nobeni)'}</strong>, drugod so šifre
                dvomestne.
              </p>
              <div className="tabela-ovoj">
                <TabelaPostavk
                  postavke={pregled.postavkeCf}
                  nadrejene={pregled.nadrejene.cf}
                  trimestni={pregled.trimestni.cf}
                  nazivNadrejene="grupa CF"
                  naShrani={(postavka, konti) => {
                    izvedi(api.shraniPostavkoCf(id, postavka, konti));
                  }}
                  naPonastavi={(postavka) => {
                    izvedi(api.ponastaviPostavkoCf(id, postavka));
                  }}
                  naDodaj={(postavka) => {
                    izvedi(api.dodajPostavkoCf(id, postavka));
                  }}
                />
              </div>
            </section>

            <section className="tabela-odsek">
              <h2>Vrste temeljnic</h2>
              <p className="namig">
                Vrste, ki se dejansko pojavijo v uvoženih knjižbah tega podjetja (pri Pantheonu
                znaki 3–5 ključa temeljnice, pri Minimaxu stolpec <em>Tem.</em>). Privzeto so
                vse <strong>navadne</strong> in nič se ne izloča. <strong>Otvoritev</strong>: knjižbe
                te vrste gredo v obdobje 0 — velja za nove uvoze, na že uvoženih letih pa šele, ko
                jo uveljaviš z gumbom pod tabelo. <strong>Zapiranje leta (izkaz)</strong>: zaključna
                temeljnica, ki zapre konte izkaza na kapital; izkaz poslovnega izida, Hitri pregled,
                Primerjava in izid v denarnem toku jo izločijo takoj, bilanca stanja jo obdrži.{' '}
                <strong>Zapiranje bilance</strong>: temeljnica, ki na koncu leta zapre bilančne konte
                na nič (zrcalna slika otvoritve); izločena je povsod — v bilanci, denarnem toku,
                kazalnikih, razčlenitvi ter pri ujemanju in izračunu otvoritve — ko knjižbe ponovno
                razvrstiš z gumbom pod tabelo. Vloga velja za vsa leta tega podjetja.
              </p>
              {pregled.vrsteTemeljnic.length === 0 ? (
                <p className="drobno">V uvoženih knjižbah ni vrst temeljnic.</p>
              ) : (
                <div className="tabela-ovoj">
                  <table className="nastavitve">
                    <thead>
                      <tr>
                        <th>Vrsta</th>
                        <th className="stevilka">Knjižb</th>
                        <th>Vloga</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pregled.vrsteTemeljnic.map((v) => (
                        <tr key={v.vrsta}>
                          <td>
                            <code>{v.vrsta}</code>
                          </td>
                          <td className="stevilka">{v.knjizb.toLocaleString('sl-SI')}</td>
                          <td>
                            <select
                              value={v.vloga}
                              aria-label={`Vloga vrste ${v.vrsta}`}
                              onChange={(d) => {
                                izvedi(api.shraniVlogoTemeljnice(id, v.vrsta, d.target.value));
                              }}
                            >
                              {VLOGE_TEMELJNIC.map((vloga) => (
                                <option key={vloga} value={vloga}>
                                  {NAZIV_VLOGE[vloga]}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {pregled.zapiranjeBilance.length > 0 && (
                <div className="opozorilo">
                  <p>
                    Vloga zapiranja bilance še ni uveljavljena na uvoženih letih. Številke v
                    poročilih se spremenijo šele, ko knjižbe ponovno razvrstiš (gumb spodaj).
                  </p>
                  <ul>
                    {pregled.zapiranjeBilance.map((l) => (
                      <li key={l.leto}>
                        <strong>{l.leto}</strong>:{' '}
                        {[
                          l.vPorocilih > 0 ? `${knjizb(l.vPorocilih)} zapiranja bilance je še v poročilih` : null,
                          l.manjka > 0 ? `${knjizb(l.manjka)} ni v poročilih, čeprav vrsta nima več te vloge` : null,
                        ]
                          .filter((d) => d !== null)
                          .join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="gumbi">
                <button
                  type="button"
                  className="gumb"
                  title={NAMIG_RAZVRSCANJA}
                  onClick={prerazvrsti}
                  disabled={razvrscam}
                >
                  {razvrscam ? 'Razvrščam …' : 'Ponovno razvrsti knjižbe'}
                </button>
                <span className="drobno">{OPIS_RAZVRSCANJA}</span>
              </div>
              {izid !== null && <IzidRazvrscanja izid={izid} />}
              <UskladitevOtvoritve
                podjetje={id}
                otvoritev={pregled.vrsteTemeljnic
                  .filter((v) => v.vloga === 'otvoritev')
                  .map((v) => v.vrsta)
                  .join(',')}
              />
            </section>

            <section className="tabela-odsek">
              <h2>Prenos izida ob letnem prehodu</h2>
              <p className="namig">
                Ko se otvoritev naslednjega leta izračuna iz preteklega (Business Central),
                se konti izkaza poslovnega izida ne prenesejo — njihov skupni saldo je izid
                preteklega leta in gre na ta konto. Pri slovenskih strankah je to skoraj vedno{' '}
                <strong>930</strong> (prenesen čisti dobiček iz prejšnjih let). Vpišeš lahko
                celotno šifro konta iz knjig (na primer <strong>930000</strong>) ali krajšo — če
                krajši šifri ustreza natanko en konto v knjigah, se uporabi ta.
              </p>
              <div className="obrazec">
                <label>
                  Konto za prenos izida
                  <input
                    type="text"
                    inputMode="numeric"
                    value={kontoIzida}
                    onChange={(d) => {
                      setKontoIzida(d.target.value);
                    }}
                  />
                </label>
                <div className="gumbi">
                  <button
                    type="button"
                    className="gumb"
                    disabled={
                      kontoIzida.trim() === '' || kontoIzida === (pregled.prenosIzida.konto ?? '')
                    }
                    onClick={() => {
                      izvedi(api.shraniPrenosIzida(id, kontoIzida.trim()));
                    }}
                  >
                    Shrani
                  </button>
                  {pregled.prenosIzida.prilagojeno && (
                    <button
                      type="button"
                      className="gumb drobni"
                      onClick={() => {
                        izvedi(api.ponastaviPrenosIzida(id));
                      }}
                    >
                      Ponastavi na standardno
                    </button>
                  )}
                </div>
              </div>
            </section>

            <section className="kartica nevarno">
              <h2>Ponastavitev</h2>
              <p>
                Odstrani vse prilagoditve tega podjetja; razvrščanje se vrne na standardno shemo.
              </p>
              <button
                type="button"
                className="gumb"
                onClick={() => {
                  izvedi(api.ponastaviVse(id));
                }}
              >
                Ponastavi vse na standardno shemo
              </button>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
